/**
 * Pure footer-credit extraction: "who built this site", read off the markup.
 *
 * Imported by build-template-developers.js and its tests. No I/O, no DOM — the
 * crawled pages are strings on disk and the live pages are strings from fetch,
 * so this walks the markup with a tag/text tokenizer instead. Attribute text is
 * never scanned: an `alt="… built by Practical Site Design"` is not a credit.
 *
 * A credit is one of three shapes:
 *   A) the phrase sits in a text node and the name follows in an anchor —
 *      `Designed And Hand Coded By <a href="…">Oak Harbor Web Designs</a>`
 *   B) the phrase sits INSIDE the anchor text —
 *      `<a href="…">Designed & Hand Coded By UI Compass</a>`
 *   C) there is no phrase at all, just the CodeStitch copyright convention —
 *      `© Copyright 2023 - <a class="cs-copyright-link" href="…">Oak Harbor Web Designs</a>`
 * A and B reduce to "the text after the `by`". C is recognised by the credit
 * class plus an OFF-SITE href: the same markup with `href="/"` is the business
 * naming itself, which is the common case and must not be read as a studio.
 */

/**
 * Words that, near a `by`, mean the sentence is about who BUILT the site.
 * "powered by" is deliberately absent: across the whole corpus it named a
 * platform or an acquiring brand ("Vibrant Cans, now powered by Bubble Binz")
 * and never once a studio the phrase list did not already catch.
 */
export const CREDIT_KEYWORD =
  /\b(?:design(?:ed|s)?|develop(?:ed|ment)?|cod(?:ed|ing)|hand[\s-]?coded|built|build|craft(?:ed)?|made|website|web\s*site|web\s*design)\b/i;

/** …and words that mean it is about something else entirely. */
const DISQUALIFY_KEYWORD =
  /\b(?:provided|operated|owned|managed|serviced|trained|installed|approved|backed|used|sold|inspired|reviewed|written|funded|insured|licensed|offered|regulated|prepared|supplied|accepted|caused|suffered)\b/i;

/** Site builders and plugins credit themselves the same way a studio does. */
const PLATFORMS = new Set([
  'shopify',
  'wix',
  'squarespace',
  'wordpress',
  'webflow',
  'duda',
  'godaddy',
  'weebly',
  'netlify',
  'vercel',
  'rebuy',
  'elementor',
  'divi',
  'hubspot',
  'google',
  'blogger',
  'ghost',
  'bigcommerce',
  'woocommerce',
  'squareup',
  'square',
  'jimdo',
  'strikingly',
  'framer',
  'carrd',
  'codestitch'
]);

/** Starter-kit filler that survives into published demo sites. */
const PLACEHOLDERS = new Set([
  'yourcompanyname',
  'companyname',
  'yourbusiness',
  'yourbusinessname',
  'yourname',
  'businessname',
  'loremipsum',
  'clientname'
]);

/** Lowercase function words: a candidate containing one is prose, not a name. */
const PROSE_WORDS = new Set([
  'a',
  'an',
  'the',
  'our',
  'your',
  'their',
  'his',
  'her',
  'its',
  'who',
  'which',
  'that',
  'this',
  'these',
  'those',
  'using',
  'with',
  'if',
  'you',
  'we',
  'they',
  'is',
  'are',
  'was',
  'were',
  'of',
  'to',
  'from',
  'as',
  'it',
  'any',
  'some',
  'both',
  'each',
  'more',
  'most',
  'than',
  'then',
  'when',
  'while',
  'because',
  'so'
]);

/** Hosts a footer links for reasons that are not authorship. */
const NON_STUDIO_HOSTS =
  /(?:^|\.)(?:facebook|instagram|twitter|x|linkedin|youtube|tiktok|pinterest|threads|nextdoor|yelp|bbb|google|goo\.gl|maps\.app\.goo\.gl|g\.page|apple|paypal|stripe|shopify|wix|squarespace|wordpress|netlify|vercel|godaddy|trustpilot|angi|houzz|thumbtack)\.[a-z.]+$/i;

/** Classes CodeStitch-derived footers put on the studio link. */
export const CREDIT_CLASS = /\b(?:cs-)?(?:copyright|credit|designer|developer)(?:[-_]?link)?\b/i;

/** Elements that never wrap anything, so they never push a nesting scope. */
const VOID_TAGS = new Set([
  'br',
  'img',
  'input',
  'meta',
  'link',
  'hr',
  'source',
  'track',
  'wbr',
  'area',
  'base',
  'col',
  'embed',
  'param'
]);

/** Inline elements a credit phrase may close or open before reaching the name. */
const INLINE_TAGS = new Set([
  'span',
  'em',
  'strong',
  'b',
  'i',
  'small',
  'p',
  'br',
  'font',
  'u',
  'sup',
  'sub',
  'label'
]);

const ENTITIES = {
  amp: '&',
  nbsp: ' ',
  quot: '"',
  apos: "'",
  lt: '<',
  gt: '>',
  copy: '©',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  middot: '·',
  bull: '•'
};

export function decodeEntities(text) {
  return String(text)
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => safeCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => safeCodePoint(parseInt(dec, 10)))
    .replace(/&([a-z]+);/gi, (whole, name) => ENTITIES[name.toLowerCase()] ?? whole);
}

function safeCodePoint(code) {
  if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return '';
  try {
    return String.fromCodePoint(code);
  } catch {
    return '';
  }
}

export function squish(text) {
  return decodeEntities(text).replace(/\s+/g, ' ').trim();
}

/**
 * Tokenize markup into tags and text runs, dropping the CONTENT of script and
 * style (a JS string containing "Designed by X" is not a rendered credit).
 */
export function tokenize(html) {
  const re = /<!--[\s\S]*?-->|<\/?([a-zA-Z][\w:-]*)((?:"[^"]*"|'[^']*'|[^'">])*)>/g;
  const out = [];
  let last = 0;
  let mute = null;
  let match;
  while ((match = re.exec(html))) {
    if (match.index > last && !mute)
      out.push({ type: 'text', value: html.slice(last, match.index) });
    last = re.lastIndex;
    const name = match[1] ? match[1].toLowerCase() : null;
    if (!name) continue;
    const close = match[0].startsWith('</');
    if (mute) {
      if (close && name === mute) mute = null;
      continue;
    }
    if (!close && (name === 'script' || name === 'style' || name === 'template')) {
      mute = name;
      continue;
    }
    out.push({ type: 'tag', name, close, attrs: match[2] || '' });
  }
  if (last < html.length && !mute) out.push({ type: 'text', value: html.slice(last) });
  return out;
}

export function attr(attrs, key) {
  const re = new RegExp(`\\b${key}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s"'>]+))`, 'i');
  const m = re.exec(attrs || '');
  if (!m) return '';
  return decodeEntities(m[2] ?? m[3] ?? m[4] ?? '').trim();
}

/**
 * The tail of the document where a credit can live. Prefers the LAST <footer>
 * through to the end — some sites put the credit in a sibling div right after
 * the footer closes — then a `.cs-bottom`/`.credit` container, then the last
 * stretch of the body.
 */
export function footerRegion(html) {
  const lower = html.toLowerCase();
  const footer = lower.lastIndexOf('<footer');
  if (footer !== -1) return html.slice(footer);
  for (const marker of ['cs-bottom', 'cs-copyright', 'cs-credit', 'class="credit"', 'copyright']) {
    const at = lower.lastIndexOf(marker);
    if (at !== -1) return html.slice(Math.max(0, at - 400));
  }
  const bodyEnd = lower.lastIndexOf('</body>');
  const end = bodyEnd === -1 ? html.length : bodyEnd;
  return html.slice(Math.max(0, end - 10000), end);
}

/**
 * Reduce a raw candidate to a studio name, or '' if it reads as prose.
 * `Designed & Hand Coded By UI Compass` reduces to `UI Compass`: the phrase is
 * stripped wherever it appears, because sites nest it inside the anchor.
 */
export function cleanName(raw) {
  let name = squish(raw);
  // Strip a leading credit phrase — the anchor text often repeats it.
  const inner = /^(.{0,48}?)\bby\b\s*:?\s*/i.exec(name);
  if (inner && CREDIT_KEYWORD.test(inner[1])) name = name.slice(inner[0].length);
  // Cut at a sentence break or a separator; keep commas (Porchlight Web, LLC).
  name = name.split(/(?:\.\s|\.$|\||©|–|—|\/\/|\s-\s)/)[0];
  name = name.replace(/^[\s\-–—:•·]+/, '').replace(/[\s,.:;\-–—•·]+$/, '');
  name = name.replace(/\s*[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]+\s*$/u, '').trim();
  if (!name || name.length > 48) return '';

  const firstAlpha = name.match(/[A-Za-z]/);
  if (!firstAlpha) return '';
  if (firstAlpha[0] !== firstAlpha[0].toUpperCase()) return '';

  const words = name.split(/\s+/);
  if (words.length > 6) return '';
  for (const word of words) {
    const bare = word.replace(/[^A-Za-z']/g, '');
    if (!bare) continue;
    if (bare === bare.toLowerCase() && PROSE_WORDS.has(bare.toLowerCase())) return '';
  }
  const bare = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (PLATFORMS.has(bare)) return '';
  if (PLACEHOLDERS.has(bare)) return '';
  return name;
}

/** Collect the text of the anchor starting at `start`, plus its href and end index. */
function readAnchor(tokens, start) {
  const open = tokens[start];
  if (!open || open.type !== 'tag' || open.name !== 'a' || open.close) return null;
  let text = '';
  let end = tokens.length;
  for (let i = start + 1; i < tokens.length; i++) {
    const token = tokens[i];
    if (token.type === 'text') text += token.value;
    else if (token.name === 'a') {
      end = i;
      break;
    }
  }
  return { text, href: attr(open.attrs, 'href'), end };
}

/** The text run immediately before `from`, looking back past inline markup only. */
function textBefore(tokens, from) {
  for (let i = from - 1; i >= 0 && i > from - 12; i--) {
    const token = tokens[i];
    if (token.type === 'text') {
      const text = squish(token.value);
      if (text) return text;
      continue;
    }
    if (!INLINE_TAGS.has(token.name)) return '';
  }
  return '';
}

/** Walk forward past inline markup to the next anchor, or null if prose intervenes. */
function anchorAfter(tokens, from) {
  for (let i = from; i < tokens.length && i < from + 12; i++) {
    const token = tokens[i];
    if (token.type === 'text') {
      if (squish(token.value)) return null;
      continue;
    }
    if (token.name === 'a' && !token.close) return readAnchor(tokens, i);
    if (!INLINE_TAGS.has(token.name)) return null;
  }
  return null;
}

/**
 * Every credit-looking attribution in the page's footer region.
 * Each is { name, url, phrase, kind } — `kind` records how it was read so a
 * reviewer can tell a linked studio credit from a bare line of text.
 *
 * `selfHost` enables shape C; without it the class-based credit link is skipped,
 * because there is then no way to tell an off-site studio link from a self-link.
 */
export function extractFooterCredits(html, { selfHost = '' } = {}) {
  if (!html) return [];
  const tokens = tokenize(footerRegion(html));
  const found = [];
  const seen = new Set();
  /** Open-element stack, so an anchor knows whether a credit container wraps it. */
  const scopes = [];

  const push = (name, url, phrase, kind) => {
    const cleaned = cleanName(name);
    if (!cleaned) return;
    const key = `${cleaned.toLowerCase()}|${url}`;
    if (seen.has(key)) return;
    seen.add(key);
    found.push({ name: cleaned, url, phrase: squish(phrase), kind });
  };

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    if (token.type === 'tag' && token.name !== 'a' && !VOID_TAGS.has(token.name)) {
      if (token.close) {
        const at = scopes.findLastIndex((scope) => scope.name === token.name);
        if (at !== -1) scopes.length = at;
      } else {
        scopes.push({ name: token.name, credit: CREDIT_CLASS.test(attr(token.attrs, 'class')) });
      }
    }

    if (token.type === 'tag' && token.name === 'a' && !token.close) {
      const anchor = readAnchor(tokens, i);
      const text = squish(anchor?.text || '');

      // Shape B: the whole credit lives inside one anchor.
      const by = /\bby\b\s*:?\s*/i.exec(text);
      if (by && CREDIT_KEYWORD.test(text.slice(0, by.index)) && !DISQUALIFY_KEYWORD.test(text)) {
        const phrase = text.slice(0, by.index + by[0].length);
        // Some footers link only the phrase and leave the name outside the
        // anchor: `<a href="…">Designed & Hand Coded By </a>Oak Harbor Web Designs`.
        const inside = text.slice(by.index + by[0].length);
        const trailing = tokens[anchor.end + 1];
        const after = inside.trim() || (trailing?.type === 'text' ? squish(trailing.value) : '');
        push(after, anchor.href, phrase, 'anchor-phrase');
        continue;
      }

      // Shape C: the credit class, plus an off-site href.
      const inCredit =
        scopes.some((scope) => scope.credit) || CREDIT_CLASS.test(attr(token.attrs, 'class'));
      const host = hostOf(anchor?.href);
      // If the text right before the anchor ends in a `by` that shape A already
      // declined — "Vibrant Cans, now powered by …" — the class does not make it
      // a credit. Shape A saw the phrase and said no; C does not get to overrule.
      const lead = textBefore(tokens, i);
      const declined = /\bby\b\s*:?\s*$/i.test(lead) && !CREDIT_KEYWORD.test(lead);
      if (
        selfHost &&
        inCredit &&
        !declined &&
        host &&
        host !== selfHost &&
        !host.endsWith(`.${selfHost}`) &&
        !selfHost.endsWith(`.${host}`) &&
        !NON_STUDIO_HOSTS.test(host)
      ) {
        push(text, anchor.href, '', 'credit-link');
      }
      continue;
    }
    if (token.type !== 'text') continue;

    // Shape A: the phrase is in the text, the name follows.
    const text = squish(token.value);
    const byRe = /\bby\b\s*:?\s*/gi;
    let by;
    while ((by = byRe.exec(text))) {
      const before = text.slice(Math.max(0, by.index - 48), by.index);
      if (!CREDIT_KEYWORD.test(before) || DISQUALIFY_KEYWORD.test(before)) continue;
      const phrase = `${before}${by[0]}`;
      const after = text.slice(by.index + by[0].length);
      if (after.trim()) {
        push(after, '', phrase, 'text');
        continue;
      }
      const anchor = anchorAfter(tokens, i + 1);
      if (anchor) push(anchor.text, anchor.href, phrase, 'anchor');
    }
  }
  return found;
}

/** The single best credit for a page, or null. Linked credits outrank bare text. */
export function pickCredit(credits, { selfHost = '' } = {}) {
  const ranked = [...credits].sort((a, b) => rank(b, selfHost) - rank(a, selfHost));
  return ranked[0] || null;
}

const KIND_RANK = { 'anchor-phrase': 3, anchor: 3, 'credit-link': 2, text: 1 };

function rank(credit, selfHost) {
  let score = KIND_RANK[credit.kind] || 0;
  if (credit.url) score += 4;
  if (credit.url && hostOf(credit.url) && hostOf(credit.url) !== selfHost) score += 3;
  return score;
}

export function hostOf(url) {
  if (!url) return '';
  const m = /^(?:https?:)?\/\/([^/?#]+)/i.exec(url.trim());
  if (!m) return '';
  return m[1]
    .toLowerCase()
    .replace(/^www\./, '')
    .replace(/:\d+$/, '');
}

/** Stable grouping key: the studio's own domain when it links one, else its name. */
export function developerId({ name, url }) {
  const host = hostOf(url);
  if (host) return host;
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Comparison form of a studio name — for matching an unlinked credit to a linked one. */
export function normalizeDeveloperName(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

/**
 * Assign every credit a grouping id, letting a LINKED credit adopt the
 * unlinked ones that share its name. Some footers link the studio and some
 * only name it; without this, "Oak Harbor Web Designs" is two rows in the
 * dropdown — one keyed by domain, one by name slug — and neither is wrong
 * enough to notice.
 */
export function assignDeveloperIds(credits) {
  const nameToHost = new Map();
  for (const credit of credits) {
    const host = hostOf(credit?.url);
    if (!host) continue;
    const key = normalizeDeveloperName(credit.name);
    if (key && !nameToHost.has(key)) nameToHost.set(key, host);
  }
  return credits.map((credit) => {
    if (!credit) return credit;
    const host = hostOf(credit.url);
    if (host) return { ...credit, id: host };
    const adopted = nameToHost.get(normalizeDeveloperName(credit.name));
    return { ...credit, id: adopted || developerId(credit) };
  });
}
